import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

interface CollapsibleSectionProps {
  title: string;
  icon?: ReactNode;
  /** Texto curto à direita do título (ex.: contagem). */
  badge?: ReactNode;
  defaultOpen?: boolean;
  /** Chamado quando o estado muda — usado para habilitar o fetch preguiçoso. */
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

/**
 * Seção colapsável leve (sem dependência externa). Mantém estado próprio e avisa o
 * consumidor via onOpenChange, permitindo carregamento preguiçoso do conteúdo.
 */
export function CollapsibleSection({ title, icon, badge, defaultOpen = false, onOpenChange, children }: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    onOpenChange?.(next);
  };

  return (
    <div className="rounded-lg border border-ds-border bg-ds-surface2 overflow-hidden">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-ds-surface transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-ds-text-primary">
          {icon}
          {title}
          {badge != null && <span className="text-xs font-normal text-ds-text-secondary">{badge}</span>}
        </span>
        <ChevronDown className={`h-4 w-4 text-ds-text-secondary shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="border-t border-ds-border p-3">{children}</div>}
    </div>
  );
}
